'use client';

import { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { useI18n } from '@/providers/i18n';
import { Loader2Icon, X } from 'lucide-react';
import { toast } from 'sonner';
import { openVideoForCapture, type VideoCaptureSession } from '@/hooks/use-extract-frame';
import {
  MIN_SUBTITLE_AREA_PX,
  type SubtitleArea,
} from '@flowmuse/models/video/baidu-vod/subtitle-areas';

// 内部用归一化矩形（0-1，相对视频画面）存储，与显示尺寸解耦；确认时按源分辨率换算像素。
type NormRect = { x: number; y: number; width: number; height: number };

type SubtitleAreaDialogProps = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  videoUrl: string;
  /** 确认选区：坐标为源视频像素（x/y/width/height） */
  onConfirm: (areas: SubtitleArea[]) => void;
};

/**
 * 手动字幕选区弹窗：抽取视频画面作底图，拖拽框选一个或多个待擦除区域。
 * 字幕未必出现在首帧，可用时间滑杆切换预览画面；选区为归一化坐标，切换后保留。
 * 视频经截帧会话只加载一次，换帧仅 seek。用于百度 VOD 字幕擦除的 manual 模式（区域内全擦除）。
 */
export default function SubtitleAreaDialog({
  open,
  onOpenChange,
  videoUrl,
  onConfirm,
}: SubtitleAreaDialogProps) {
  const { t } = useI18n();
  const [frame, setFrame] = useState<{ dataUrl: string; width: number; height: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [frameSec, setFrameSec] = useState(0);
  const [maxSec, setMaxSec] = useState(0);
  const [rects, setRects] = useState<NormRect[]>([]);
  const [draft, setDraft] = useState<NormRect | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);
  // 指针事件间的最新 draft（避免依赖渲染闭包时序）
  const draftRef = useRef<NormRect | null>(null);
  // 截帧会话：打开弹窗时加载一次视频，换预览帧只 seek，不重复下载
  const sessionRef = useRef<VideoCaptureSession | null>(null);
  // 截帧请求序号：慢请求返回时若已有更新的请求则丢弃结果
  const captureSeqRef = useRef(0);

  useEffect(() => {
    if (!open) return;
    setFrame(null);
    setRects([]);
    setDraft(null);
    draftRef.current = null;
    setFrameSec(0);
    setMaxSec(0);
    setLoading(true);
    let disposed = false;
    openVideoForCapture(videoUrl)
      .then(async (session) => {
        if (disposed) {
          session.dispose();
          return;
        }
        sessionRef.current = session;
        setMaxSec(session.duration);
        const dataUrl = await session.captureAt(0);
        if (!disposed) setFrame({ dataUrl, width: session.width, height: session.height });
      })
      .catch(() => {
        if (disposed) return;
        toast.error(t('Failed to load video frame'));
        onOpenChange(false);
      })
      .finally(() => {
        if (!disposed) setLoading(false);
      });
    return () => {
      disposed = true;
      captureSeqRef.current++;
      sessionRef.current?.dispose();
      sessionRef.current = null;
    };
    // onOpenChange/t 为父组件每次渲染的新引用，不参与依赖，避免重复加载视频
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, videoUrl]);

  /** 切换预览时间：复用会话 seek + 截帧 */
  const seekPreview = (sec: number) => {
    const session = sessionRef.current;
    if (!session) return;
    const seq = ++captureSeqRef.current;
    setLoading(true);
    session
      .captureAt(sec)
      .then((dataUrl) => {
        if (captureSeqRef.current !== seq) return;
        setFrame({ dataUrl, width: session.width, height: session.height });
      })
      .catch(() => {
        // 切换失败保留当前画面
        if (captureSeqRef.current === seq) toast.error(t('Failed to load video frame'));
      })
      .finally(() => {
        if (captureSeqRef.current === seq) setLoading(false);
      });
  };

  const getNormPoint = (e: React.PointerEvent): { x: number; y: number } => {
    const rect = containerRef.current!.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height)),
    };
  };

  const setDraftBoth = (value: NormRect | null) => {
    draftRef.current = value;
    setDraft(value);
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!frame || e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const p = getNormPoint(e);
    dragStartRef.current = p;
    setDraftBoth({ x: p.x, y: p.y, width: 0, height: 0 });
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const start = dragStartRef.current;
    if (!start) return;
    const p = getNormPoint(e);
    setDraftBoth({
      x: Math.min(start.x, p.x),
      y: Math.min(start.y, p.y),
      width: Math.abs(p.x - start.x),
      height: Math.abs(p.y - start.y),
    });
  };

  const handlePointerUp = () => {
    const done = draftRef.current;
    dragStartRef.current = null;
    setDraftBoth(null);
    if (!done || !frame) return;
    const widthPx = done.width * frame.width;
    const heightPx = done.height * frame.height;
    if (widthPx < MIN_SUBTITLE_AREA_PX || heightPx < MIN_SUBTITLE_AREA_PX) return;
    setRects((prev) => [...prev, done]);
  };

  const handleConfirm = () => {
    if (!frame || rects.length === 0) return;
    const areas: SubtitleArea[] = rects.map((r) => ({
      x: Math.round(r.x * frame.width),
      y: Math.round(r.y * frame.height),
      width: Math.max(1, Math.round(r.width * frame.width)),
      height: Math.max(1, Math.round(r.height * frame.height)),
    }));
    onConfirm(areas);
  };

  const rectStyle = (r: NormRect): React.CSSProperties => ({
    left: `${r.x * 100}%`,
    top: `${r.y * 100}%`,
    width: `${r.width * 100}%`,
    height: `${r.height * 100}%`,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogTitle className="text-sm font-semibold">{t('Select erase areas')}</DialogTitle>
        {loading && !frame && (
          <div className="flex h-64 items-center justify-center text-muted-foreground">
            <Loader2Icon size={20} className="animate-spin" />
          </div>
        )}
        {frame && (
          <div
            ref={containerRef}
            className="relative mx-auto w-fit max-w-full cursor-crosshair touch-none select-none overflow-hidden rounded-lg"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
          >
            <img
              src={frame.dataUrl}
              alt=""
              className="block max-h-[64vh] max-w-full"
              draggable={false}
            />
            {rects.map((r, index) => (
              <div
                key={index}
                className="pointer-events-none absolute border-2 border-primary bg-primary/15"
                style={rectStyle(r)}
              >
                {/* 删除按钮放框内角落，避免被容器 overflow-hidden 裁掉 */}
                <button
                  type="button"
                  className="pointer-events-auto absolute right-0.5 top-0.5 flex size-5 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm hover:opacity-80"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={() => setRects((prev) => prev.filter((_, i) => i !== index))}
                  aria-label={t('Remove area')}
                >
                  <X size={12} />
                </button>
              </div>
            ))}
            {draft && (
              <div
                className="pointer-events-none absolute border-2 border-dashed border-primary bg-primary/10"
                style={rectStyle(draft)}
              />
            )}
            {loading && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-background/40">
                <Loader2Icon size={20} className="animate-spin text-muted-foreground" />
              </div>
            )}
          </div>
        )}
        {frame && maxSec > 0 && (
          <div className="flex items-center gap-3">
            <div className="h-8 w-full flex items-center">
              <Slider
                min={0}
                max={maxSec}
                step={0.1}
                value={[Math.max(0, Math.min(maxSec, frameSec))]}
                onValueChange={(v) => {
                  const val = Array.isArray(v) ? Number(v[0]) : Number(v as any);
                  if (Number.isFinite(val)) setFrameSec(val);
                }}
                onValueCommit={(v) => {
                  const val = Array.isArray(v) ? Number(v[0]) : Number(v as any);
                  if (Number.isFinite(val)) seekPreview(Math.max(0, Math.min(maxSec, val)));
                }}
              />
            </div>
            <div className="shrink-0 w-[52px] rounded-lg bg-sidebar-accent/50 px-2 py-1 text-right font-mono text-xs tabular-nums text-foreground/70">
              {Math.max(0, Math.min(maxSec, frameSec)).toFixed(1)}s
            </div>
          </div>
        )}
        <div className="flex items-center justify-between gap-3">
          <div className="text-xs text-muted-foreground">
            {t('Drag on the frame to mark areas to erase')}
          </div>
          <div className="flex shrink-0 gap-2">
            <Button
              variant="ghost"
              size="sm"
              disabled={rects.length === 0}
              onClick={() => setRects([])}
            >
              {t('Clear')}
            </Button>
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
              {t('Cancel')}
            </Button>
            <Button size="sm" disabled={!frame || rects.length === 0} onClick={handleConfirm}>
              {t('Confirm')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
